// A supplier's record (UC-PURCHASING-003 steps 5–6, -004, -006, -007; /purchasing/suppliers/{id}, …/lab-lines, …/options) —
// beyond `purchasing.html`'s Supplier panel the prototype draws none, so it is designed from the use cases: its details, ordering,
// terms, VAT, discount and Hoyalog data, the account numbers of the branches the reader may read, the products whose supplier it
// is (read from the catalogue), its price lists (read from pricing, UC-PRICING-009), and "Not connected" to accounting until
// Sprints 9–10. The Lab lines tab lists the codes its
// applied price lists carry on a day; Coatings, tints & PPF its lens component options as the catalogue keeps them. An Owner or
// Admin edits, deactivates and reactivates it, and deletes one recorded in error through the Control PIN. A stock role sees its
// goods receipts (UC-PURCHASING-003 A3, UC-PURCHASING-008); a Receptionist neither sees them nor asks for them.
import { LAB_LINE_DESIGN_TYPES, LAB_LINE_KINDS, LAB_LINE_STATUS_LABELS, LENS_COMPONENT_OPTION_KIND_LABELS, ORDER_CHANNEL_LABELS, type LabLineKind, type LabLineView, type SupplierView } from "@visionx/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { lensOptions, products } from "../api/endpoints.catalogue";
import { suppliers } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime, formatDecimal, formatMoney, isLocalDate, percentFromBp } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Chip, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Pager, Panel, Select, StatusChip, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { InactiveChip, LENS_DESIGN_LABEL, refusalWords, supplierReferenceRefusal, supplierTypesText } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { ImportedFrom } from "./ImportsShared";
import { PriceListsPanel } from "./PriceLists";
import { useDebouncedValue, useLeaveGuard } from "./PatientsShared";
import { SupplierReceiptsPanel } from "./GoodsReceipts";
import { PurchasingTabs } from "./Purchasing";

const dash = <span className="faint">—</span>;
const TABS = [["details", "Details"], ["lab-lines", "Lab lines"], ["options", "Coatings, tints & PPF"]] as const;
type Tab = (typeof TABS)[number][0];

export function SupplierRecord() {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { id = "", tab: rawTab } = useParams();
  const tab: Tab = TABS.some(([t]) => t === rawTab) ? (rawTab as Tab) : "details";
  const block = useWriteBlock();
  const online = block === null;
  const title = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const canWrite = can(s, "purchasing.supplier.write");
  const read = useQuery({ queryKey: ["supplier", id], queryFn: () => suppliers.get(id) });
  const [dialog, setDialog] = useState<"deactivate" | "delete" | null>(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "crit"; text: ReactNode } | null>(null);
  const [busy, setBusy] = useState(false);
  const state = location.state as { notice?: string } | null;
  useEffect(() => {
    if (!state?.notice) return;
    setNotice({ tone: "ok", text: <b>{state.notice}</b> });
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.notice]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["supplier", id] }); void qc.invalidateQueries({ queryKey: ["suppliers"] }); };
  if (read.isPending) return <section className="screen" id="s-supplier"><div className="content"><Loading what="the supplier" /></div></section>;
  if (read.isError) {
    return (
      <section className="screen" id="s-supplier"><div className="content">
        <PageHead title="Supplier" actions={<Link className="btn" to="/purchasing/suppliers">Suppliers</Link>} />
        {isApiError(read.error) && read.error.code === "NOT_FOUND" ? <Alert tone="warn"><b>This supplier was not found.</b> It may have been deleted, or belong to another organisation.</Alert>
          : isApiError(read.error) && read.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Suppliers" error={read.error} /> : <ErrorAlert error={read.error} />}
      </div></section>
    );
  }
  const v = read.data.data;
  const etag = read.data.etag;
  const reactivate = async () => {
    setBusy(true);
    try { await suppliers.activate(v.id); setNotice({ tone: "ok", text: <b>{v.name} reactivated.</b> }); refresh(); }
    catch (err) { setNotice({ tone: "crit", text: <><b>Not reactivated.</b> {describeError(err)}</> }); }
    finally { setBusy(false); }
  };
  return (
    <section className="screen" id="s-supplier">
      <div className="content">
        <PageHead
          title={<>{v.name} <StatusChip status={v.status} /></>}
          sub={<>{supplierTypesText(v.types)} · <Link to="/purchasing/suppliers">Suppliers</Link></>}
          actions={canWrite && <>
            {online ? <Link className="btn" to={`/purchasing/suppliers/${v.id}/edit`}>Edit</Link> : <button className="btn" type="button" disabled title={title}>Edit</button>}
            {v.status === "Active"
              ? <button className="btn" type="button" disabled={!online} title={title} onClick={() => setDialog("deactivate")}>Deactivate</button>
              : <button className="btn" type="button" disabled={!online || busy} title={title} onClick={() => void reactivate()}>Reactivate</button>}
            <button className="btn danger" type="button" disabled={!online} title={title} onClick={() => setDialog("delete")}>Delete supplier…<LockMark /></button>
          </>}
        />
        <PurchasingTabs />
        {notice && <Alert tone={notice.tone} actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice.text}</Alert>}
        <div className="tabs" aria-label="Supplier">
          {TABS.map(([t, label]) => <NavLink key={t} to={t === "details" ? `/purchasing/suppliers/${v.id}` : `/purchasing/suppliers/${v.id}/${t}`} end className={t === tab ? "active" : ""}>{label}</NavLink>)}
        </div>
        {tab === "details" && <Details v={v} />}
        {tab === "lab-lines" && <LabLines supplierId={v.id} />}
        {tab === "options" && <Options supplier={v} />}
        {dialog === "deactivate" && <DeactivateSupplierModal v={v} onClose={() => setDialog(null)} onDone={() => { setDialog(null); setNotice({ tone: "ok", text: <><b>{v.name} deactivated.</b> It leaves the default list and every picker; everything already linked to it keeps it.</> }); refresh(); }} />}
        {dialog === "delete" && (
          <DeleteSupplierModal v={v} etag={etag} onClose={() => setDialog(null)} onConflict={refresh} onDeactivate={() => setDialog("deactivate")} onDone={(name) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["suppliers"] });
            qc.removeQueries({ queryKey: ["supplier", v.id] });
            navigate("/purchasing/suppliers", { state: { notice: `${name} deleted.` } });
          }} />
        )}
      </div>
    </section>
  );
}

function Details({ v }: { v: SupplierView }) {
  const s = useSessionView();
  const supplied = useQuery({ queryKey: ["products", "of-supplier", v.id], queryFn: () => products.list({ supplierId: v.id, includeInactive: true }, { limit: 20 }) });
  const address = [v.address.line1, v.address.line2, v.address.city, v.address.province, v.address.postalCode, v.address.country !== "ZA" ? v.address.country : null].filter(Boolean).join(", ");
  const items = supplied.data?.data.items ?? [];
  return (
    <div className="grid-2">
      <div className="stack" style={{ gap: 16 }}>
        <Panel title="Details">
          <KV rows={[
            ["Types", supplierTypesText(v.types)],
            ["Contact", v.contactName ?? dash],
            ["Phone", v.phone ?? dash],
            ["Email", v.email ?? dash],
            ["Address", address || dash],
            ["Status", v.status === "Active" ? "Active" : <>Inactive{v.deactivatedBy ? ` · ${v.deactivatedBy.displayName}` : ""}{v.deactivatedAt ? ` · ${formatDateTime(v.deactivatedAt)}` : ""}{v.deactivationReason ? ` · ${v.deactivationReason}` : ""}</>],
            ...(v.notes ? [["Notes", v.notes] as [ReactNode, ReactNode]] : []),
          ]} />
        </Panel>
        <Panel title="Ordering" meta="purchase orders from Sprint 7">
          <KV rows={[
            ["Orders by", <>{ORDER_CHANNEL_LABELS[v.orderChannel]}{v.orderChannel === "EmailPdf" && v.orderEmail ? <> to {v.orderEmail}</> : null}{v.leadTimeDays !== null ? ` · ${v.leadTimeDays} working day${v.leadTimeDays === 1 ? "" : "s"}` : ""}</>],
            ["Copies to", v.orderEmailCc.length > 0 ? v.orderEmailCc.join(", ") : dash],
          ]} />
        </Panel>
        <Panel title="Terms and VAT">
          <KV rows={[
            ["Terms", v.paymentTermsDays !== null ? `${v.paymentTermsDays} days` : dash],
            ["VAT", v.isVatRegistered ? <>VAT registered{v.vatNumber ? <> · <span className="mono">{v.vatNumber}</span></> : null}</> : "Not VAT registered"],
            ["Default discount", percentFromBp(v.defaultDiscountBp)],
          ]} />
        </Panel>
        <Panel title="Hoyalog" meta="not used — roadmap">
          <KV rows={[
            ["Manufacturer code", <span className="mono">{v.hoyalog.manufacturerCode}</span>],
            ["Enabled", v.hoyalog.enabled ? "Yes" : "No"],
            ["Country code", <span className="mono">{v.hoyalog.countryCode}</span>],
            ["Lens catalogue", v.lensCatalogueCode ? <span className="mono">{v.lensCatalogueCode}</span> : dash],
          ]} />
        </Panel>
      </div>
      <div className="stack" style={{ gap: 16 }}>
        <Panel title="Branch accounts" meta="the branches you may read" tight>
          {v.branchAccounts.length === 0 ? <Empty title="No branch account yet.">An Owner or Admin adds them under Settings › Branches.</Empty> : (
            <table>
              <thead><tr><th>Branch</th><th>Account</th><th>Default for</th></tr></thead>
              <tbody>
                {v.branchAccounts.map((a) => (
                  <tr key={a.id}><td>{a.branch.name}</td><td className="mono">{a.accountNumber}</td><td>{[a.isDefaultForLenses ? "lenses" : null, a.isDefaultForFitting ? "fitting" : null].filter(Boolean).join(" · ") || <span className="faint">—</span>}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        <Panel title="Products" meta={supplied.data ? `${items.length}${supplied.data.data.nextCursor ? "+" : ""} whose supplier it is` : undefined} tight
          foot={<Link to={`/products?supplier=${v.id}&inactive=true`}>Products filtered by {v.name}</Link>}>
          {supplied.isPending && <div className="panel-body"><Loading what="its products" /></div>}
          {supplied.isError && <div className="panel-body"><ErrorAlert error={supplied.error} /></div>}
          {supplied.data && (items.length === 0 ? <Empty title="No product names this supplier yet." /> : (
            <div className="list">
              {items.map((p) => (
                <div key={p.id} className="item">
                  <div className="grow"><div className="primary"><Link to={`/products/${p.id}`}><span className="mono">{p.sku}</span></Link> {p.displayName}</div></div>
                  {p.status === "Inactive" && <InactiveChip />}
                </div>
              ))}
            </div>
          ))}
        </Panel>
        {can(s, "purchasing.po.read") && <SupplierReceiptsPanel supplierId={v.id} supplierName={v.name} />}
        <PriceListsPanel supplierId={v.id} title="Price lists" meta={v.types.includes("LensLab") ? "its lab cost lists" : "a lens lab's cost lists"} importable={v.types.includes("LensLab") && v.status === "Active"} />
        <Panel title="Accounting"><KV rows={[["Vendor", "Not connected · suppliers reach the accounting system from Sprints 9–10"]]} /></Panel>
      </div>
    </div>
  );
}

const indexText = (x1000: number | null): string | null => (x1000 === null ? null : formatDecimal(x1000 / 1000, 2));
/** A lab line's kind, as the catalogue's options name them (a Colour is a tint). */
const LAB_LINE_KIND_WORD: Readonly<Record<LabLineKind, string>> = { Lens: "Lens", Coating: "Coating", Colour: "Tint", PPF: "PPF" };
/** The refraction index as the API reads it (`1.67`). */
const INDEX_RE = /^[12][.][0-9]{1,3}$/;

/** UC-PURCHASING-006: the lines in force on a day, read-only; the filters in the address. */
function LabLines({ supplierId }: { supplierId: string }) {
  const [sp, setSp] = useSearchParams();
  // A practice of one store: a line's list is the organisation's, so its scope is named only where a line's is the store's own
  // (UC-PURCHASING-006 A7; change 13).
  const oneStore = useOneStore();
  const [typed, setTyped] = useState(sp.get("q") ?? "");
  const q = useDebouncedValue(typed.trim(), 250);
  const design = (LAB_LINE_DESIGN_TYPES as readonly string[]).includes(sp.get("design") ?? "") ? sp.get("design")! : "";
  const kind = (LAB_LINE_KINDS as readonly string[]).includes(sp.get("kind") ?? "") ? (sp.get("kind") as LabLineKind) : "";
  const asAt = isLocalDate(sp.get("asAt")) ? sp.get("asAt")! : "";
  // UC-PURCHASING-006 step 4: the index as written on the lens, 1.67; anything else is said under the box and not sent.
  const [indexTyped, setIndexTyped] = useState(sp.get("index") ?? "");
  const indexWanted = useDebouncedValue(indexTyped.trim(), 250);
  const indexOk = indexWanted === "" || INDEX_RE.test(indexWanted);
  const index = indexOk ? indexWanted : (INDEX_RE.test(sp.get("index") ?? "") ? sp.get("index")! : "");
  useEffect(() => {
    if (!indexOk || (sp.get("index") ?? "") === indexWanted) return;
    setSp((p) => { const n = new URLSearchParams(p); if (indexWanted) n.set("index", indexWanted); else n.delete("index"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexWanted, indexOk]);
  useEffect(() => {
    if ((sp.get("q") ?? "") === q) return;
    setSp((p) => { const n = new URLSearchParams(p); if (q) n.set("q", q); else n.delete("q"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const set = (k: string, v: string) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  useEffect(() => { setCursors([]); }, [q, design, kind, index, asAt]);
  const list = useQuery({
    queryKey: ["suppliers", supplierId, "lab-lines", q, design, kind, index, asAt, cursor ?? ""],
    queryFn: () => suppliers.labLines(supplierId, { q: q || undefined, design: design || undefined, kind: kind || undefined, index: index || undefined, asAt: asAt || undefined }, { cursor }), placeholderData: keepPreviousData,
  });
  const rows = list.data?.data.items ?? [];
  const scoped = !oneStore || rows.some((l) => l.priceList.branch !== null);
  return (
    <>
      <div className="toolbar">
        <div className="input w-search"><Icon name="search" /><input type="search" aria-label="Search lab lines" placeholder="Code or a word of the description" value={typed} onChange={(e) => setTyped(e.target.value)} /></div>
        <Select aria-label="Kind" boxClass="w-sel" value={kind} onChange={(e) => set("kind", e.target.value)}>
          <option value="">Kind · All</option>
          {LAB_LINE_KINDS.map((k) => <option key={k} value={k}>Kind · {LAB_LINE_KIND_WORD[k]}</option>)}
        </Select>
        <Select aria-label="Design" boxClass="w-sel" value={design} onChange={(e) => set("design", e.target.value)}>
          <option value="">Design · All</option>
          {LAB_LINE_DESIGN_TYPES.map((t) => <option key={t} value={t}>Design · {LENS_DESIGN_LABEL[t]}</option>)}
        </Select>
        <div className="field">
          <TextInput aria-label="Index" mono inputMode="decimal" boxClass="w-80" placeholder="Index" value={indexTyped} error={!indexOk} onChange={(e) => setIndexTyped(e.target.value)} />
          {!indexOk && <div className="error" role="alert">An index such as 1.67</div>}
        </div>
        <TextInput type="date" aria-label="In force on" mono value={asAt} onChange={(e) => set("asAt", e.target.value)} />
        <span className="right small faint">{asAt ? `As they stood on ${formatDate(`${asAt}T12:00:00.000Z`)}` : "In force today"} · written by applied price lists only</span>
      </div>
      {list.isPending && <Loading what="the lab lines" />}
      {list.isError && <ErrorAlert error={list.error} />}
      {list.data && (
        <Panel tight foot={<>
          <span className="small faint">{oneStore ? "A code a newer list drops is flagged “Not in latest price list” and its lens hidden from new orders, unless another current list names it" : "A branch's own list wins at that branch; a code a newer list drops is flagged “Not in latest price list” and its lens hidden from new orders, unless another current list names it"}</span>
          <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data.data.nextCursor!])} />
        </>}>
          {rows.length === 0 ? <Empty title={q || design || kind || index ? "No lab line matches." : "No lab lines."}>{q || design || kind || index ? "Clear a filter or choose another day." : "Lab lines are written when one of the supplier's price lists is applied."}</Empty> : (
            <div className="tbl-scroll">
              <table>
                <thead><tr><th>Code</th><th>Kind</th><th>Description</th><th>Index · design</th><th className="r">Lab price ex VAT</th><th>List</th>{scoped && <th>Scope</th>}<th>Status</th><th>Source</th></tr></thead>
                <tbody>{rows.map((l) => <LabLineRow key={l.id} l={l} scoped={scoped} oneStore={oneStore} />)}</tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}

function LabLineRow({ l, scoped, oneStore }: { l: LabLineView; scoped: boolean; oneStore: boolean }) {
  const idx = indexText(l.refractionIndexX1000);
  const design = l.designType ? LENS_DESIGN_LABEL[l.designType].toLowerCase() : null;
  return (
    <tr>
      <td className="mono nw">{l.code}</td>
      <td className="nw">{LAB_LINE_KIND_WORD[l.kind]}</td>
      <td>{l.description ?? dash}{l.product && <div className="small faint"><Link to={`/products/${l.product.id}`}>{l.product.sku}</Link></div>}</td>
      <td className="nw">{[idx, design].filter(Boolean).join(" · ") || dash}</td>
      <td className="money">{l.netCostCents === null ? "—" : formatMoney(l.netCostCents)}{l.discountBp ? <div className="small faint">list {formatMoney(l.listCostCents)} less {percentFromBp(l.discountBp)}</div> : null}</td>
      <td className="nw">from {formatDate(`${l.effectiveFrom}T12:00:00.000Z`)}<div className="small faint">{l.priceList.name}</div></td>
      {scoped && <td className="nw">{l.priceList.branch ? l.priceList.branch.name : oneStore ? "Organisation" : "All branches"}{l.appliesHere && l.priceList.branch ? <div className="small faint">applies here</div> : null}</td>}
      <td>{l.status === "Current" ? LAB_LINE_STATUS_LABELS.Current : <Chip tone="warn">{LAB_LINE_STATUS_LABELS.NotInLatestPriceList}</Chip>}</td>
      <td className="small">{l.source ? <ImportedFrom importJobId={l.source.importJobId} fileName={l.source.fileName} rowNo={l.source.rowNo} /> : dash}</td>
    </tr>
  );
}

/** UC-PURCHASING-006 A5: the supplier's lens component options as the catalogue keeps them, read-only here. */
function Options({ supplier }: { supplier: SupplierView }) {
  const list = useQuery({ queryKey: ["lens-options", supplier.id, "record"], queryFn: () => lensOptions.list({ supplierId: supplier.id, includeInactive: true }) });
  const rows = list.data?.data.items ?? [];
  return (
    <Panel tight foot={<span className="help">Options are changed in the catalogue — <Link to={`/products/lens-options?supplier=${supplier.id}`}>Products › Lens options</Link>.</span>}>
      {list.isPending && <div className="panel-body"><Loading what="the options" /></div>}
      {list.isError && <div className="panel-body"><ErrorAlert error={list.error} /></div>}
      {list.data && (rows.length === 0 ? <Empty title="No coatings, tints or PPF.">A lens lab's options are added in the catalogue.</Empty> : (
        <table>
          <thead><tr><th>Kind</th><th>Code</th><th>Description</th><th className="r">Cost ex VAT</th><th className="r">Price incl. VAT</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id}>
                <td>{LENS_COMPONENT_OPTION_KIND_LABELS[o.kind]}</td>
                <td className="mono">{o.supplierCode}</td>
                <td>{o.description}</td>
                <td className="money">{o.costCents === null ? "—" : formatMoney(o.costCents)}</td>
                <td className="money">{formatMoney(o.sellingPriceCents)}</td>
                <td>{o.isActive ? "Active" : "Inactive"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </Panel>
  );
}

/** UC-PURCHASING-004: an optional reason; no Control PIN. */
function DeactivateSupplierModal({ v, onClose, onDone }: { v: SupplierView; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // While it is sent the dialog cannot be closed — Cancel, Escape — so its answer, a failure included, is always shown here.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { await suppliers.deactivate(v.id, reason.trim() || null); onDone(); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Deactivate ${v.name}`} meta="no Control PIN" onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy}>{busy ? "Deactivating…" : "Deactivate"}</button>
    </>}>
      {error !== null && <Alert tone="crit"><b>Not deactivated.</b> {isApiError(error) ? error.field("reason")?.message ?? refusalWords(error) : describeError(error)}</Alert>}
      <Alert tone="info">{v.name} cannot be chosen for anything new — a product, an option, a band, a price-list import, a receipt or a branch account new to a list — while everything already linked to it keeps it. It can be reactivated.</Alert>
      <Field label="Reason" htmlFor="supplier-deactivate-reason" help="Optional; shown on the record with who and when">
        <TextInput id="supplier-deactivate-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * UC-PURCHASING-007: the reason, then the Control PIN (`Delete`, record type `supplier`) under one Idempotency-Key per reason;
 * a supplier any record names is refused before any PIN, naming the kinds in words, with Deactivate offered instead.
 */
function DeleteSupplierModal({ v, etag, onClose, onDeactivate, onDone, onConflict }: { v: SupplierView; etag: string | null; onClose: () => void; onDeactivate: () => void; onDone: (name: string) => void; onConflict: () => void }) {
  const withStepUp = useStepUp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState(false);
  useLeaveGuard(working);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setError(null); }, [reason]);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    if (!etag) { setError(new Error("The supplier was read without a version; reload and try again.")); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => { if (g.token) setWorking(true); return suppliers.void(v.id, why, etag, { ...g, idempotencyKey: key }); }, {
        title: `Delete supplier ${v.name}`, reason: "none",
        description: `${v.name} leaves every list and picker and its name is free again; its record is kept. The reason is written to the Control Log.`,
      });
      if (r) onDone(r.data.name);
    } catch (err) {
      // Pressed again after a lost answer, under the reason's key, answered as carried out: deleted once, its answer not kept — as
      // for its 200, with no second PIN (UC-PURCHASING-007 A3 step 2; the verification of round 3's fixes).
      if (carriedOut(err)) { onDone(v.name); return; }
      setError(err);
      // Changed meanwhile: the supplier is read again at once, so the dialog's next try carries its new version.
      if (isApiError(err) && err.code === "VERSION_CONFLICT") onConflict();
    } finally { setBusy(false); setWorking(false); }
  };
  const refusal = supplierReferenceRefusal(error);
  // An inactive supplier is not offered Deactivate (UC-PURCHASING-007 E1): it is inactive already.
  const inactive = v.status === "Inactive";
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  return (
    <Modal title={`Delete ${v.name}`} meta="Owner or Admin · recorded in error" onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>{refusal !== null && inactive ? "Close" : "Cancel"}</button>
      {refusal !== null
        ? (inactive ? null : <button className="btn primary" type="button" onClick={onDeactivate}>Deactivate</button>)
        : <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Deleting…" : "Delete supplier"}<LockMark /></button>}
    </>}>
      {working && <Alert tone="info" role="status"><b>Deleting {v.name}…</b> Keep this page open; the suppliers list opens when it is done.</Alert>}
      {refusal !== null
        ? <Alert tone="crit"><b>{v.name} cannot be deleted: {refusal} name it.</b> {inactive ? "It is inactive already: it is offered for nothing new, and everything linked to it keeps it." : "Deactivate it instead."}</Alert>
        : error !== null && !reasonErr && <Alert tone="crit"><b>Not deleted.</b> {isApiError(error) && error.code === "VERSION_CONFLICT" ? "The supplier was changed since you opened it; it has been read again — check it, and delete it again if it is still to go." : describeError(error)}</Alert>}
      <Alert tone="warn"><b>For a supplier that should never have existed</b> — captured twice, before anything named it. It is voided, never removed: who, when and why are kept with it.</Alert>
      <Field label="Reason" required htmlFor="supplier-delete-reason" error={reasonErr} help="Written on the record and to the Control Log with the authoriser">
        <TextArea id="supplier-delete-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <div className="help"><LockMark>requires Control PIN</LockMark> — an Owner or Admin authorises the deletion.</div>
    </Modal>
  );
}
